"use client"
import { motion } from "framer-motion"
import { BadgeCheck, Users, Rocket, Award } from "lucide-react"

// The count arrives as a prop rather than being computed here: this is a
// client component, and deriving displayed values in two places is how the
// server and client HTML end up disagreeing.
const buildStats = (certificationCount: number) => [
  {
    icon: <BadgeCheck className="h-8 w-8 text-primary" />,
    label: `${certificationCount} Professional Certifications`,
  },
  { icon: <Rocket className="h-8 w-8 text-primary" />, label: "Financial Crime Presales at NiCE Actimize" },
  { icon: <Users className="h-8 w-8 text-primary" />, label: "Fortune 500 & Enterprise Delivery" },
  { icon: <Award className="h-8 w-8 text-primary" />, label: "Founder of TUNE (AI SaaS)" },
]

export default function AboutSection({ certificationCount }: { certificationCount: number }) {
  const stats = buildStats(certificationCount)

  return (
    <section id="about" className="section-padding bg-secondary/50">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="heading-responsive font-bold text-center mb-8 font-display">About Me</h2>
          <div className="text-responsive text-center text-muted-foreground max-w-3xl mx-auto mb-12 leading-relaxed space-y-4">
            <p>
              I&apos;m a technical presales engineer at NiCE Actimize, where I design fraud, AML, and KYC solutions
              and run proof-of-concepts for banking clients across APAC — translating regulatory and risk
              requirements into working architectures and demos.
            </p>
            {/* No duration claims in here. This paragraph previously said
                "three years at Deloitte" (it was seventeen months) and called
                the Doerscircle role full-stack development (it was solutions
                architecture) — both contradicted by the timeline further down
                the same page. Naming the work rather than counting the years
                keeps this from drifting again. */}
            <p>
              My spine is unusual for presales: I founded TUNE, an AI fine-tuning and deployment platform for SMEs,
              moved from engineer to solutions architect at Doerscircle, then delivered identity and risk programmes
              (including SailPoint) for large enterprises at Deloitte — picking up graph data science with Neo4j and
              hands-on GenAI and RAG engineering along the way.
            </p>
            <p>
              I&apos;m heading toward the intersection of AI and financial crime: building RAG applications, graph
              analytics, and data pipelines, and currently working toward the Databricks Data Engineer Associate and
              Databricks Generative AI Engineer Associate certifications.
            </p>
          </div>
          <div className="grid-responsive text-center gap-4 sm:gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="p-6 bg-background rounded-xl border border-border/80 shadow-md hover:shadow-lg hover:border-primary/15 hover:-translate-y-0.5 transition-all duration-300"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.45, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="flex justify-center mb-3 text-primary">{stat.icon}</div>
                <p className="font-semibold text-sm sm:text-base">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}
